import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useStore } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { inr } from "@/lib/format";
import { toast } from "sonner";
import { ShoppingCart } from "lucide-react";

export default function StorefrontProduct() {
  const { slug, pid } = useParams();
  const { tenant } = useStore();
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const [duration, setDuration] = useState("daily");
  const [units, setUnits] = useState(1);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => { api.get(`/store/${slug}/products/${pid}`).then(({ data }) => setProduct(data)); }, [slug, pid]);

  if (!product) return <div className="max-w-6xl mx-auto px-4 py-16 text-center opacity-60">Loading…</div>;
  const b = tenant?.branding || {};
  const pricing = product.pricing || {};
  const availableDurations = [
    pricing.hourly && { v: "hourly", l: `Hourly – ${inr(pricing.hourly)}/hr` },
    pricing.daily && { v: "daily", l: `Daily – ${inr(pricing.daily)}/day` },
    pricing.weekly && { v: "weekly", l: `Weekly – ${inr(pricing.weekly)}/wk` },
    pricing.monthly && { v: "monthly", l: `Monthly – ${inr(pricing.monthly)}/mo` },
    pricing.yearly && { v: "yearly", l: `Yearly – ${inr(pricing.yearly)}/yr` },
    pricing.custom_per_day && { v: "custom", l: `Custom dates – ${inr(pricing.custom_per_day)}/day` },
  ].filter(Boolean);

  let rate = pricing[duration];
  if (duration === "custom") rate = pricing.custom_per_day || pricing.daily;
  let calcUnits = units;
  if (duration === "custom" && startDate && endDate) {
    const sd = new Date(startDate), ed = new Date(endDate);
    calcUnits = Math.max(1, Math.ceil((ed - sd) / (1000 * 60 * 60 * 24)));
  }
  const subtotal = (rate || 0) * calcUnits * qty;
  const deposit = (product.deposit || 0) * qty;

  const addToCart = () => {
    try {
      const key = `cart_${slug}`;
      const raw = localStorage.getItem(key);
      const items = raw ? JSON.parse(raw) : [];
      items.push({
        product_id: product.id,
        product_name: product.name,
        product_image: product.images?.[0],
        qty,
        duration_type: duration,
        units: calcUnits,
        start_date: startDate,
        end_date: endDate || null,
        rate,
        deposit,
        is_recurring: pricing.recurring_enabled && duration === "monthly",
      });
      localStorage.setItem(key, JSON.stringify(items));
      window.dispatchEvent(new Event("cart_update"));
      toast.success("Added to cart");
      nav(`/t/${slug}/checkout`);
    } catch (e) { toast.error("Could not add to cart"); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="t-card t-surface overflow-hidden aspect-square" style={{ background: b.theme === "automotive" ? "#1a1a1a" : "#f8fafc" }}>
          {product.images?.[0] && <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />}
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider opacity-60">{product.category?.name} • {product.brand?.name}</div>
          <h1 className="text-3xl md:text-4xl font-bold mt-1" style={{ fontFamily: b.heading_font, color: b.primary_color }} data-testid="product-title">{product.name}</h1>
          <p className="mt-3 opacity-80">{product.description}</p>

          <Card className="t-card mt-6 p-5 t-surface space-y-4">
            <div>
              <Label>Rental Duration</Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger data-testid="product-duration-select"><SelectValue /></SelectTrigger>
                <SelectContent>{availableDurations.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {duration === "custom" ? (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="product-start-date" /></div>
                <div><Label>End</Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="product-end-date" /></div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start date</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
                <div><Label>Units ({duration})</Label><Input type="number" min={1} value={units} onChange={(e) => setUnits(Math.max(1, +e.target.value))} data-testid="product-units-input" /></div>
              </div>
            )}
            <div><Label>Quantity</Label><Input type="number" min={1} value={qty} onChange={(e) => setQty(Math.max(1, +e.target.value))} data-testid="product-qty-input" /></div>
            <div className="border-t border-slate-200 pt-3 space-y-1 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><b>{inr(subtotal)}</b></div>
              <div className="flex justify-between"><span>Refundable deposit</span><b>{inr(deposit)}</b></div>
              <div className="flex justify-between text-base mt-1" style={{ color: b.primary_color }}><b>Total payable now</b><b>{inr(subtotal + deposit)}</b></div>
            </div>
            <Button className="w-full t-btn" onClick={addToCart} style={{ background: b.primary_color, color: "#fff" }} data-testid="product-add-to-cart-btn">
              <ShoppingCart className="h-4 w-4 mr-2" /> Add to Cart & Continue
            </Button>
          </Card>

          {product.specifications?.length > 0 && (
            <Card className="mt-4 p-5 t-card t-surface">
              <h3 className="font-bold mb-2">Specifications</h3>
              <ul className="text-sm space-y-1">{product.specifications.map((s, i) => <li key={i} className="flex justify-between"><span className="opacity-60">{s.key}</span><span>{s.value}</span></li>)}</ul>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
